import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { AlertTriangle, RotateCcw } from "lucide-react";

const OPTIONS = [30, 40, 50, 60, 70, 80, 90];

/**
 * ThresholdDefaultTile — Settings tile that lets a tradie tune the fleet-wide
 * cost-code alert threshold (percent of budget consumed before a push fires
 * on any job that hasn't set its own override). Wired to
 * GET/PUT /api/settings/alert-threshold. The Jobs page reads the same
 * endpoint so its "Default (X%)" label always mirrors reality.
 */
export default function ThresholdDefaultTile() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/alert-threshold");
      setState(data);
    } catch (err) {
      devLog.warn("threshold default load failed", err);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (val) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/alert-threshold", {
        threshold_pct: val,
      });
      setState(data);
      toast.success(
        val === null
          ? `Reset · using system default ${data.system_default}%`
          : `Alerts now fire at ${data.effective}% of budget`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  return (
    <section
      data-testid="threshold-default-tile"
      className="rounded-xl bi-elevate p-4 space-y-3"
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-[#FF9500]/12 text-[#FF9500] flex items-center justify-center">
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-heading font-extrabold text-white leading-tight">
            Default alert threshold
          </div>
          <div className="text-[11px] text-white/50 leading-snug mt-0.5">
            Fires when a job&apos;s spend hits{" "}
            <b
              data-testid="threshold-default-effective"
              className="text-[#FF9500]"
            >
              {state.effective}%
            </b>{" "}
            of budget. Per-job overrides win.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {OPTIONS.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => save(v)}
            disabled={busy}
            data-testid={`threshold-default-opt-${v}`}
            className={`h-8 px-2.5 rounded-full text-[11px] font-heading font-bold uppercase tracking-wider transition ${
              state.effective === v
                ? "bg-[#FF9500]/20 text-[#FF9500] border border-[#FF9500]/40"
                : "bi-ghost-btn text-white/60"
            }`}
          >
            {v}%
          </button>
        ))}
        {state.user_default !== null && (
          <button
            type="button"
            onClick={() => save(null)}
            disabled={busy}
            data-testid="threshold-default-reset"
            className="h-8 px-2.5 rounded-full text-[11px] inline-flex items-center gap-1 bi-ghost-btn text-white/45"
            title="Reset to system default"
          >
            <RotateCcw className="w-3 h-3" />
            Reset
          </button>
        )}
      </div>
    </section>
  );
}
